<template>
  <v-menu v-model="menu" bottom left offset-y transition="slide-y-transition" nudge-left="10" :close-on-content-click="false">
    <template v-slot:activator="{ on, attrs }">
      <v-btn icon v-bind="attrs" v-on="on" class="mr-3" :aria-label="bellAriaLabel">
        <v-tooltip bottom color="black">
          <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <v-badge
              :value="unreadCount > 0"
              :content="badgeContent"
              color="error"
              overlap
              bordered
            >
              <v-icon v-bind="tooltipAttrs" v-on="tooltipOn" color="black">mdi-bell-outline</v-icon>
            </v-badge>
          </template>
          <span>{{ 'notifications'|localize }}</span>
        </v-tooltip>
      </v-btn>
    </template>
    <v-card width="380">
      <div class="d-flex align-center justify-space-between px-4 py-3">
        <span class="text-subtitle-1 font-weight-bold">{{ 'notifications'|localize }}</span>
        <v-btn v-if="unreadCount > 0" text small color="primary" :loading="markingAll" @click="markAllAsRead">
          {{ 'markAllAsRead'|localize }}
        </v-btn>
      </div>
      <v-divider/>
      <v-list v-if="items.length" dense class="py-0 notification-list">
        <v-list-item
          v-for="item in items"
          :key="item.id"
          link
          tabindex="0"
          role="button"
          :aria-label="notificationText(item).title"
          :class="{'notification-item--unread': !item.read_at}"
          @click="openItem(item)"
          @keydown.enter="openItem(item)"
          @keydown.space.prevent="openItem(item)"
        >
          <v-list-item-icon class="mr-3">
            <v-avatar size="36" :color="NOTIFICATION_TYPE_COLORS[item.type]">
              <v-icon dark size="18">{{ NOTIFICATION_TYPE_ICONS[item.type] }}</v-icon>
            </v-avatar>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="text-body-2 font-weight-medium text-wrap">{{ notificationText(item).title }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption text-wrap">{{ notificationText(item).summary }}</v-list-item-subtitle>
            <div class="text-caption grey--text mt-1">{{ item.created_at|relativeTime }}</div>
          </v-list-item-content>
          <v-list-item-action v-if="!item.read_at" class="align-self-start mt-2 ml-1">
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  :aria-label="'markAsRead'|localize"
                  @click.stop="markRead(item)"
                >
                  <v-icon color="primary" size="18">mdi-email-outline</v-icon>
                </v-btn>
              </template>
              <span>{{ 'markAsRead'|localize }}</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <div v-else-if="error" class="text-center grey--text text-caption py-8 px-4">
        <div class="mb-2">{{ 'notificationsLoadError'|localize }}</div>
        <v-btn text x-small color="primary" @click="getItems">{{ 'retry'|localize }}</v-btn>
      </div>
      <div v-else-if="!loading" class="text-center grey--text text-caption py-8 px-4">
        {{ 'emptyNotificationsTitle'|localize }}
      </div>
      <div v-else class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="24" :width="2"/>
      </div>
      <v-divider/>
      <div class="text-center py-2">
        <v-btn text small color="primary" @click="menu = false" :to="{name: 'notifications'}">
          {{ 'seeAllNotifications'|localize }}
        </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import notificationsApi from '@/api/notificationsApi';
  import resolveNotificationText from '@/helpers/notificationText';
  import {NOTIFICATION_TYPE_ICONS, NOTIFICATION_TYPE_COLORS} from '@/enums/notificationTypes';

  const DROPDOWN_ITEMS_LIMIT = 8;
  const POLL_INTERVAL_MS = 60000;

  export default {
    name: 'NotificationsDropdown',

    data() {
      return {
        NOTIFICATION_TYPE_ICONS,
        NOTIFICATION_TYPE_COLORS,
        menu: false,
        items: [],
        loading: false,
        error: false,
        markingAll: false,
        pollTimer: null,
      };
    },

    computed: {
      unreadCount() {
        return this.$store.getters.unreadNotificationsCount;
      },
      badgeContent() {
        return this.unreadCount > 99 ? '99+' : this.unreadCount;
      },
      bellAriaLabel() {
        return `${localize('notifications')}, ${this.unreadCount} ${localize('unread')}`;
      },
      userRole() {
        return this.$store.getters.userData && this.$store.getters.userData.role;
      },
    },

    watch: {
      menu(isOpen) {
        if (isOpen) this.getItems();
      },
    },

    mounted() {
      this.$store.dispatch('fetchUnreadNotificationsCount');
      this.pollTimer = setInterval(() => {
        this.$store.dispatch('fetchUnreadNotificationsCount');
      }, POLL_INTERVAL_MS);
    },

    beforeDestroy() {
      clearInterval(this.pollTimer);
    },

    methods: {
      async getItems() {
        try {
          this.loading = true;
          this.error = false;
          let response = await notificationsApi.getNotifications({tab: 'all', per_page: DROPDOWN_ITEMS_LIMIT});
          this.items = (response.data.data || []).slice(0, DROPDOWN_ITEMS_LIMIT);
          // `unread` is the user's global unread count, independent of filters — refresh
          // the badge from it too, not just from the polling interval.
          this.$store.commit('setUnreadNotificationsCount', response.data.unread || 0);
        } catch (e) {
          // Distinct from a genuinely empty inbox — see the template's error/empty branches.
          this.items = [];
          this.error = true;
        } finally {
          this.loading = false;
        }
      },
      async markAllAsRead() {
        try {
          this.markingAll = true;
          await notificationsApi.markAllNotificationsRead();
          this.items = this.items.map(item => ({...item, read_at: item.read_at || new Date().toISOString()}));
          this.$store.dispatch('fetchUnreadNotificationsCount');
        } catch (e) {
          methods.showGlobalSnackbar('error', methods.getErrors(e.response));
        } finally {
          this.markingAll = false;
        }
      },
      notificationText(item) {
        return resolveNotificationText(item);
      },
      // Shared by the row click and the small mark-as-read icon — idempotent, so firing
      // it from both places at once (or twice) is harmless.
      async markRead(item) {
        if (item.read_at) return;
        try {
          await notificationsApi.markNotificationRead(item.id);
          item.read_at = new Date().toISOString();
          this.$store.dispatch('fetchUnreadNotificationsCount');
        } catch (e) {}
      },
      openItem(item) {
        this.menu = false;
        this.markRead(item);
        // System-type notifications aren't linked to a request — nothing to navigate to.
        // .catch() guards the known Vue Router 3 "NavigationDuplicated" rejection when the
        // target is already the current route.
        if (methods.isNotificationLinkable(item)) {
          this.$router.push(methods.notificationDetailsRoute(this.userRole, item.related_id)).catch(() => {});
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .notification-list {
    max-height: 420px;
    overflow-y: auto;
  }

  // Vuetify's custom theme colors (serenade, primary) are resolved at runtime through
  // component props (Colorable mixin), not as static CSS classes/vars, so a plain scoped
  // rule needs the literal hex values — same approach App.vue already uses for accents.
  .notification-item--unread {
    background-color: #fff1e5;
    border-left: 3px solid #fc8101;
  }
</style>
